import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, CalendarDays, Shield, X, Search, Users,
  MapPin, ChevronRight, Plus, Loader2, QrCode, Monitor,
  CheckCircle2, Clock, Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminOrgEventsPage() {
  const { token } = useAuth();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [org, setOrg] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [orgRes, eventsRes] = await Promise.all([
        axios.get(`${API}/api/event/admin/organizations/${orgId}`, { headers }),
        axios.get(`${API}/api/admin/org/${orgId}/events`, { headers }),
      ]);
      setOrg(orgRes.data);
      setEvents(eventsRes.data);
    } catch (e) {
      toast.error("Erreur de chargement");
      navigate("/app/event-center");
    } finally { setLoading(false); }
  }, [orgId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = events.filter(ev =>
    !search || ev.title?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-[#0099CC]" />
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-org-events-page">
      {/* Super Admin Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between" data-testid="super-admin-banner">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-amber-400" />
          <span className="text-amber-400 font-bold text-sm">MODE SUPER ADMIN</span>
          <span className="text-amber-200/70 text-sm">— Vous administrez actuellement</span>
          <span className="text-white font-semibold text-sm">{org?.org_name || "..."}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/app/event-center")}
          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
          data-testid="exit-super-admin-btn"
        >
          <X className="w-4 h-4 mr-1" /> Quitter le mode Super Admin
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/event-center/org/${orgId}`)} className="text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#0099CC]" /> Evenements — {org?.org_name}
            </h1>
            <p className="text-sm text-[#64748B] mt-0.5">{events.length} evenement(s)</p>
          </div>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-[#64748B]" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un evenement..."
            className="pl-9 bg-[#060B14] border-[#1A2332] text-white h-9"
            data-testid="events-search"
          />
        </div>
      </div>

      {/* Events Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-[#64748B]">
          <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{events.length === 0 ? "Aucun evenement cree" : "Aucun resultat"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(ev => (
            <div
              key={ev.id}
              onClick={() => navigate(`/app/event-center/org/${orgId}/event/${ev.id}`)}
              className="bg-[#0B1120] border border-[#1A2332] rounded-xl overflow-hidden hover:border-[#0099CC]/30 transition-all cursor-pointer group"
              data-testid={`event-card-${ev.id}`}
            >
              {/* Cover */}
              <div className="h-32 bg-gradient-to-br from-[#0099CC]/20 to-[#8B5CF6]/20 relative">
                {ev.cover_image_url && (
                  <img src={ev.cover_image_url} alt="" className="w-full h-full object-cover" />
                )}
                <div className="absolute top-2 right-2">
                  <Badge variant="outline" className={`text-xs backdrop-blur-sm ${
                    ev.status === "published"
                      ? "bg-green-500/20 text-green-400 border-green-400/30"
                      : ev.status === "ended"
                      ? "bg-[#8B5CF6]/20 text-[#8B5CF6] border-[#8B5CF6]/30"
                      : ev.status === "cancelled"
                      ? "bg-red-500/20 text-red-400 border-red-400/30"
                      : "bg-[#1A2332]/80 text-[#64748B] border-[#64748B]/30"
                  }`}>
                    {ev.status === "published" ? "Publie" : ev.status === "ended" ? "Termine" : ev.status === "cancelled" ? "Annule" : ev.status === "draft" ? "Brouillon" : ev.status}
                  </Badge>
                </div>
              </div>

              <div className="p-4 space-y-3">
                <h3 className="text-white font-semibold text-sm truncate group-hover:text-[#0099CC] transition-colors">{ev.title}</h3>
                <div className="space-y-1.5 text-xs text-[#64748B]">
                  {ev.event_date && (
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      {new Date(ev.event_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      {ev.start_time && ` a ${ev.start_time}`}
                    </div>
                  )}
                  {ev.location && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" /> {ev.location}
                    </div>
                  )}
                </div>

                {/* Stats bar */}
                <div className="flex items-center justify-between text-xs pt-2 border-t border-[#1A2332]">
                  <div className="flex items-center gap-3">
                    <span className="text-[#0099CC]"><Users className="w-3 h-3 inline mr-1" />{ev.total_registered || 0}</span>
                    <span className="text-[#10B981]"><CheckCircle2 className="w-3 h-3 inline mr-1" />{ev.total_checked_in || 0}</span>
                    {(ev.total_waitlisted || 0) > 0 && (
                      <span className="text-[#F59E0B]"><Clock className="w-3 h-3 inline mr-1" />{ev.total_waitlisted}</span>
                    )}
                  </div>
                  {ev.max_capacity > 0 && (
                    <span className="text-[#64748B]">/{ev.max_capacity}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
